import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, UserRoleLabels } from '../../utils/enums';

// Department-based assignment options (matching EditWorkOrder.js)
const DEPARTMENT_OPTIONS = [
  {
    value: 'property_management',
    label: 'Property Management',
    description: 'Assigns to Property Manager and Maintenance roles',
    roles: [UserRole.PROPERTY_MANAGER, UserRole.MAINTENANCE]
  },
  {
    value: 'housekeeping',
    label: 'Housekeeping',
    description: 'Assigns to Housekeeper Manager role',
    roles: [UserRole.HOUSEKEEPER_MANAGER]
  }
];

export default function WorkOrderRoleFilter({ selectedRoles, onChange, autoInitialize = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useAuth();
  
  // Convert selected roles to selected departments
  const getSelectedDepartments = () => {
    return DEPARTMENT_OPTIONS.filter(dept => 
      dept.roles.some(role => selectedRoles.includes(role))
    ).map(dept => dept.value);
  };

  // Convert selected departments to roles
  const getRolesFromDepartments = (departments) => {
    const roles = [];
    departments.forEach(deptValue => {
      const dept = DEPARTMENT_OPTIONS.find(d => d.value === deptValue);
      if (dept) {
        roles.push(...dept.roles);
      }
    });
    return [...new Set(roles)]; // Remove duplicates
  };
  
  // Initialize with user's department if none are selected - but only once on initial load
  useEffect(() => {
    // Only auto-initialize once when the component first loads
    if (!autoInitialize) {
      setHasInitialized(true);
      return;
    }

    if (!hasInitialized && user?.role) {
      // Skip auto-initialization for HOUSEKEEPER_MANAGER (handled at page level)
      if (user.role !== 'HOUSEKEEPER_MANAGER' && selectedRoles.length === 0) {
        // Find which department the user's role belongs to
        const userDepartment = DEPARTMENT_OPTIONS.find(dept => 
          dept.roles.includes(user.role)
        );
        if (userDepartment) {
          console.log('WorkOrderRoleFilter: Auto-initializing with user department:', userDepartment.value);
          onChange(userDepartment.roles);
        }
      }
      setHasInitialized(true);
    } else if (!hasInitialized && user) {
      // Mark as initialized even if user role is not assignable
      setHasInitialized(true);
    }
  }, [user, hasInitialized, selectedRoles.length, onChange, autoInitialize]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDepartmentToggle = (departmentValue) => {
    const selectedDepartments = getSelectedDepartments();
    let newDepartments;
    
    if (selectedDepartments.includes(departmentValue)) {
      // Remove department
      newDepartments = selectedDepartments.filter(dept => dept !== departmentValue);
    } else {
      // Add department
      newDepartments = [...selectedDepartments, departmentValue];
    }
    
    // Convert departments back to roles
    const newRoles = getRolesFromDepartments(newDepartments);
    onChange(newRoles);
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    const allRoles = DEPARTMENT_OPTIONS.flatMap(dept => dept.roles);
    onChange([...new Set(allRoles)]); // Remove duplicates
    setIsOpen(false);
  };

  const selectedDepartments = getSelectedDepartments();
  const selectedDepartmentObjects = DEPARTMENT_OPTIONS.filter(dept => 
    selectedDepartments.includes(dept.value)
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor="workOrderRoleFilter" className="block text-sm font-medium text-gray-700 mb-1">
        Assigned Departments
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <span className="flex items-center">
          {selectedDepartmentObjects.length > 0 ? (
            <span className="flex items-center space-x-1">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {selectedDepartmentObjects[0].label}
              </span>
              {selectedDepartmentObjects.length > 1 && (
                <span className="text-gray-500">+{selectedDepartmentObjects.length - 1}</span>
              )}
            </span>
          ) : (
            <span className="text-gray-500">All departments</span>
          )}
        </span>
        <svg
          className={`h-5 w-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div 
          className="absolute z-[80] mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
          style={{ 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Quick action buttons */}
          <div className="border-b border-gray-200 px-3 py-2">
            <div className="flex justify-between gap-2">
              <button
                type="button"
                onClick={handleClearAll}
                className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex-1 px-2 py-1 text-xs font-medium text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
              >
                Select All
              </button>
            </div>
          </div>

          {/* Department options */}
          {DEPARTMENT_OPTIONS.map(department => (
            <div
              key={department.value}
              className={`cursor-pointer select-none relative py-3 pl-3 pr-9 hover:bg-gray-100 ${
                selectedDepartments.includes(department.value) ? 'bg-green-50' : ''
              }`}
              onClick={() => handleDepartmentToggle(department.value)}
            >
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{department.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{department.description}</div>
                </div>
                {selectedDepartments.includes(department.value) && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-green-600">
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected department tags with individual remove buttons */}
      {selectedDepartmentObjects.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedDepartmentObjects.map(department => (
            <span
              key={department.value}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
            >
              {department.label}
              <button
                type="button"
                onClick={() => handleDepartmentToggle(department.value)}
                className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <span className="sr-only">Remove {department.label}</span>
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
          {selectedDepartmentObjects.length > 1 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100"
            >
              Clear All
            </button>
          )}
        </div>
      )}
      
      {/* Helper text */}
      <div className="mt-1">
        {selectedDepartments.length === 0 ? (
          <p className="text-xs text-gray-500">Showing work orders from all departments</p>
        ) : (
          <p className="text-xs text-gray-500">
            Showing work orders assigned to {selectedDepartments.length === 1 ? 'this department' : 'these departments'}
          </p>
        )}
      </div>
    </div>
  );
}
